import { BrowserLogger } from '@class/core/browser-logger';
import { ControllerInventoryMqttMsgDTO, DropletController } from './droplet-controller.model';
import { DeviceDetails } from './device-details.model';
import { DropletAttribute } from './droplet-attribute.model';
import { DropletControllerInventoryRequest } from './controller-inventory-request/droplet-controller-inventory-request.model';
import { EndpointControllerDTO, DropletType } from './../endpoint/endpoint-payload.model';
import { IMqttMessage } from 'ngx-mqtt';
import { TranslationsService } from '../../../services/common/translations.service';
import { DropletV4 } from '@utils/droplet-v4';

export class DropletOverview {
  deviceNumber: number;
  id: string;
  uuid: string;
  unit_uuid: string;
  statusKeys: string[];
  lastSeenFromServer: string;
  triggerFirmwareUpdate: boolean;
  type: string;
}

export class DropletDisplay {
  overview: DropletOverview;
  attributes: DropletAttribute;
  controllers: DropletController[];
  devices: DeviceDetails[];
  controllersOutOfSync?: boolean;

  public processControllerInventoryMqttMessage(mqttMessage: IMqttMessage): void {
    BrowserLogger.log('DropletDisplay.processControllerInventoryMqttMessage', { mqttMessage, dropletDisplay: this });
    this.controllersOutOfSync = false; // reset flag (checking each controller sync status below)
    this.removeControllersNotInDatabase();
    if (mqttMessage) {
      const controllerInventoryMqttMsg: ControllerInventoryMqttMsgDTO[] = JSON.parse(mqttMessage.payload.toString());
      if (this.controllers?.length || controllerInventoryMqttMsg?.length) {
        this.controllers.forEach((controller) => {
          controller.applyMqttInventoryMsg(controllerInventoryMqttMsg);
          // out-of-sync version check (if not already out-of-sync)
          if (!this.controllersOutOfSync && controller.controllerExistOnDroplet && !controller.controllerVersionSync) {
            this.controllersOutOfSync = true;
          }
        });
        this.addControllersFromMqttMsgNotInDatabase(controllerInventoryMqttMsg);
      }
    }
  }

  private removeControllersNotInDatabase(): void {
    BrowserLogger.log('DropletDisplay.removeControllersNotInDatabase', { dropletDisplay: this });
    // popping out the controllers which are not in database
    // they'll be added back in again once the it completes the check
    // controllerExistInDatabase this variable means that the controller is not in database
    // they are added to the list so that user can view them and remove them
    // they have been added because they exist in mqtt msg
    // so if the mqtt msg comes in, popping them out
    // if they don't exist anymore, will not be displayed
    // otherwise will be added back again
    this.controllers = this.controllers.filter((controller) => {
      // return only controllers that exist in the database
      return controller.controllerExistInDatabase;
    });
  }

  public applyEndpointControllerDTO(
    endpointControllerDTO: EndpointControllerDTO,
    translationsService: TranslationsService,
  ): DropletController {
    // Apply the controller from dbase to the droplet matching controller (replace only, not add)
    const controller = this.controllers.find((c) => c.id == endpointControllerDTO.id);
    BrowserLogger.log('DropletDisplay.applyEndpointControllerDTO', {
      droplet: this,
      endpointControllerDTO,
      controller,
    });
    if (controller) {
      const newController = EndpointControllerDTO.adaptToDropletController(endpointControllerDTO, translationsService);
      Object.assign(controller, newController);
    }
    return controller;
  }

  public applyControllersPayload(
    controllersPayload: EndpointControllerDTO[],
    translationsService: TranslationsService,
  ): void {
    BrowserLogger.log('DropletDisplay.applyControllersPayload', { droplet: this, controllersPayload });
    // Apply the updated list of controllers from dbase to the droplet
    this.controllers = EndpointControllerDTO.adaptToDropletControllers(controllersPayload, translationsService);
  }

  private addControllersFromMqttMsgNotInDatabase(mqttMessage: ControllerInventoryMqttMsgDTO[]): void {
    const missingControllers = DropletController.getMissingMqttControllers(mqttMessage, this.controllers);
    this.controllers = this.controllers.concat(missingControllers);
    BrowserLogger.log('DropletDisplay.addControllersFromMqttMsgNotInDatabase', { mqttMessage, self: this });
  }

  public static findByUuid(droplets: DropletDisplay[], uuid: string): DropletDisplay {
    const result = droplets.find((droplet) => droplet.overview.uuid === uuid);
    BrowserLogger.log('DropletDisplay.findByUuid', { result, uuid, droplets });
    return result;
  }

  public static isType(self: DropletDisplay, type: DropletType): boolean {
    const result = self?.overview?.type === type;
    BrowserLogger.log('DropletDisplay.isType', { result, type, self });
    return result;
  }

  /**
   * Enables the "Add device" functionality if the Droplet allows it.
   * Only CLASSIC physical Droplets can add devices (not V4 or cloud Droplets).
   *
   * @param droplet - The droplet to check.
   * @returns true if the droplet is a CLASSIC physical Droplet, false otherwise.
   */
  public static canAddDevice(droplet: DropletDisplay): boolean {
    if (DropletV4.isV4(droplet.overview.type, droplet.overview.unit_uuid)) {
      return DropletV4.canAddDevices();
    }

    const result = DropletDisplay.isType(droplet, DropletType.DROPLET);
    BrowserLogger.log('DropletDisplay.canAddDevice', { result, self });
    return result;
  }

  /**
   * Check if the droplet can use controller inventory.
   * Only CLASSIC physical droplets can use controller inventory (not V4 or cloud Droplets).
   *
   * @param droplet - The droplet to check.
   * @returns True if the droplet can use controller inventory, false otherwise.
   */
  public static canUseControllerInventory(droplet: DropletDisplay): boolean {
    if (DropletV4.isV4(droplet.overview.type, droplet.overview.unit_uuid)) {
      return DropletV4.canUseControllerInventory();
    }

    const result = DropletDisplay.isType(droplet, DropletType.DROPLET);
    BrowserLogger.log('DropletDisplay.canUseControllerInventory', { result, droplet });
    return result;
  }

  public static filterControllerInventoryDroplets(droplets: Array<DropletDisplay>): Array<DropletDisplay> {
    const result = droplets.filter((droplet) => {
      return DropletDisplay.canUseControllerInventory(droplet);
    });
    BrowserLogger.log('DropletDisplay.filterControllerInventoryDroplets', { result, droplets });
    return result;
  }

  public static isMyControllerInventoryRequest(
    controllerInventoryRequest: DropletControllerInventoryRequest,
    droplet: DropletDisplay,
  ): boolean {
    // simple check that a DropletControllerInventoryRequest is for the given droplet
    // this logic is used in a few places so encapsulated here
    const result = controllerInventoryRequest?.dropletUuid === droplet.overview.uuid;
    BrowserLogger.log('DropletDisplay.isMyControllerInventoryRequest', {
      result,
      controllerInventoryRequest,
      droplet,
    });
    return result;
  }
}
